import React, { useEffect, useState } from "react";
import { Box, Button, Checkbox, Dialog, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SERVICE_TYPE_OPTIONS, inServiceTypeOrder } from "./opportunityOptions";

const DARK = "#483C3B";
const ACCENT = "#E84610";

export const selectedCountText = (count) => `${count} service type${count === 1 ? "" : "s"} selected`;

// Figma "Service type" popup, multi-select (approved point 1). The choice is only applied
// by "Select service type"; Cancel, ✕ and Escape discard it.
const ServiceTypeDialog = ({ open, value, onClose, onApply }) => {
  const [draft, setDraft] = useState([]);

  useEffect(() => {
    if (open) setDraft(inServiceTypeOrder(value));
  }, [open, value]);

  const toggle = (name) =>
    setDraft((d) => inServiceTypeOrder(d.includes(name) ? d.filter((t) => t !== name) : [...d, name]));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="service-type-title"
      aria-describedby="service-type-subtitle"
      PaperProps={{ sx: { width: 720, maxWidth: "calc(100% - 32px)", m: 2, borderRadius: "8px" } }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", px: 3, pt: 3, pb: 2, borderBottom: "1px solid #EAE7E6" }}>
        <Box>
          <Typography id="service-type-title" component="h2" sx={{ fontSize: 20, fontWeight: 600, color: DARK }}>
            Service type
          </Typography>
          <Typography id="service-type-subtitle" sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.6)", mt: 0.5 }}>
            Select the category that best describes the engagement.
          </Typography>
        </Box>
        <IconButton aria-label="Close service type" onClick={onClose} size="small" sx={{ color: DARK }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, py: 1, overflowY: "auto" }}>
        {SERVICE_TYPE_OPTIONS.map(({ name, description }) => {
          const checked = draft.includes(name);
          return (
            <Box
              key={name}
              data-testid={`service-type-row-${name}`}
              onClick={() => toggle(name)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 2,
                py: 1.5,
                cursor: "pointer",
                borderBottom: "1px solid #EAE7E6",
                border: checked ? `1px solid ${DARK}` : undefined,
                borderRadius: checked ? "6px" : 0,
                backgroundColor: checked ? "#F3F1F0" : "transparent",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: DARK }}>{name}</Typography>
                <Typography sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.6)" }}>{description}</Typography>
              </Box>
              <Checkbox
                checked={checked}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggle(name)}
                inputProps={{ "aria-label": name }}
                sx={{ "&.Mui-checked": { color: ACCENT } }}
              />
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, py: 2, borderTop: "1px solid #EAE7E6" }}>
        <Typography aria-live="polite" data-testid="service-type-count" sx={{ fontSize: 13, color: "rgba(0, 0, 0, 0.6)" }}>
          {selectedCountText(draft.length)}
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button variant="outlined" onClick={onClose} sx={{ textTransform: "none", color: DARK, borderColor: "#D6D2D1" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={draft.length === 0}
            onClick={() => onApply(draft)}
            sx={{ textTransform: "none", backgroundColor: DARK, "&:hover": { backgroundColor: "#2F2726" } }}
          >
            Select service type
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ServiceTypeDialog;
